import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  MenuItem,
  Stack,
  Tab,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import BulkReviewPanel from "../components/BulkReviewPanel";
import BulkSendProgressPanel from "../components/ChatAutomations/BulkSendProgressPanel";
import MemberMessagePanel from "../components/MemberMessagePanel";
import PageHeader from "../components/PageHeader";
import ResponsiveTable from "../components/ResponsiveTable";
import {
  SCHEDULED_CANCELLATIONS,
  SEND_PERSONAL_MESSAGES,
  TRIAL_CANCELLATIONS,
} from "../gql/queries/cancellations";
import { foldText } from "../helpers/foldText";
import { messageToolLabel, RECENT_MESSAGES } from "../helpers/recentMessages";
import { Spacing } from "../types/enum";

/** Same 1:1 booking link the backend fills into {link}. */
const BOOKING_LINK = "https://calendar.app.google/zCB8L6LmdRK6Aonj9";

/** The window the tab opens on. */
const DEFAULT_DAYS = 45;

interface CancellationMember {
  userId: string;
  firstName: string;
  name: string;
  email: string;
  country?: string | null;
  endsAt?: string | null;
  endDate: string;
  daysLeft?: number | null;
  planId?: string | null;
  eventsAttended?: number | null;
  message: string;
}

interface CancellationGroup {
  key: string;
  category?: string | null;
  tier: string;
  title: string;
  subtitle?: string | null;
  template: string;
  count: number;
  members: CancellationMember[];
}

interface CancellationsResult {
  kind: string;
  days: number;
  total: number;
  groups: CancellationGroup[];
}

/** One row of the flat list: a member plus the group whose wording she gets. */
interface Row {
  member: CancellationMember;
  group: CancellationGroup;
}

/** Fill a (possibly edited) template's placeholders for one member. */
const fillMessage = (template: string, m: CancellationMember): string =>
  template
    .replace(/\{name\}/g, m.firstName || "there")
    .replace(/\{end_date\}/g, m.endDate)
    .replace(/\{link\}/g, BOOKING_LINK);

/** Download rows as a CSV file (BOM so Excel reads UTF-8). */
const downloadCsv = (filename: string, rows: string[][]): void => {
  const esc = (v: string) => `"${(v ?? "").replace(/"/g, '""')}"`;
  const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const daysLeftColour = (days?: number | null): "error" | "warning" | "default" => {
  if (days == null) {
    return "default";
  }
  if (days <= 14) {
    return "error";
  }
  if (days <= 21) {
    return "warning";
  }
  return "default";
};

interface TabProps {
  kind: "scheduled" | "trial";
  query: typeof SCHEDULED_CANCELLATIONS;
  resultField: "adminScheduledCancellations" | "adminTrialCancellations";
  campaign: string;
}

const CancellationTab: React.FC<TabProps> = ({ kind, query, resultField, campaign }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [skipDays, setSkipDays] = useState(14);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [templates, setTemplates] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** The member whose message is open for review, if any. */
  const [openFor, setOpenFor] = useState<string>("");
  const [showWording, setShowWording] = useState(false);
  /** Bulk send is a two-step: review every message, then send. */
  const [reviewing, setReviewing] = useState(false);

  // Who got an actual CHAT message lately (from any tool — the log is shared), so
  // we warn about and auto-unselect them.
  const [loadRecent, { data: recentData }] = useLazyQuery(RECENT_MESSAGES, {
    fetchPolicy: "network-only",
  });

  const [load, { data, loading }] = useLazyQuery(query, {
    fetchPolicy: "network-only",
    onCompleted: (result) => {
      const res: CancellationsResult | undefined = result?.[resultField];
      const loaded = res?.groups ?? [];
      const members = loaded.flatMap((g) => g.members);
      setTemplates(Object.fromEntries(loaded.map((g) => [g.key, g.template])));
      setSelected(new Set(members.map((m) => m.email)));
      if (members.length > 0) {
        void loadRecent({ variables: { userIds: members.map((m) => m.userId), withinDays: 60 } });
      }
    },
  });
  const [runSend, { loading: sending }] = useMutation(SEND_PERSONAL_MESSAGES);
  const [sendId, setSendId] = useState<string | null>(null);

  // Load once on open, so the tab isn't a blank screen with a button on it.
  // Deliberately the constant, not `days`: this is the first load, and depending
  // on the state would re-query on every keystroke in the "ending within" box.
  useEffect(() => {
    void load({ variables: { days: DEFAULT_DAYS } });
  }, [load]);

  const result: CancellationsResult | undefined = data?.[resultField];
  const groups = useMemo(() => result?.groups ?? [], [result]);

  /** Every member as a flat row — one list to scan, not a stack of cards. */
  const allRows = useMemo(
    () => groups.flatMap((group) => group.members.map((member) => ({ member, group }))),
    [groups],
  );

  const recentByUser = useMemo(() => {
    const map = new Map<string, { days: number; campaign?: string | null; count: number }>();
    for (const row of recentData?.adminRecentOutreach ?? []) {
      const ago = Math.floor((Date.now() - new Date(row.lastAt).getTime()) / 86_400_000);
      map.set(row.userId, { days: ago, campaign: row.lastCampaign, count: row.count });
    }
    return map;
  }, [recentData]);

  const contactedWithinSkip = (userId: string): boolean => {
    const r = recentByUser.get(userId);
    return r !== undefined && r.days <= skipDays;
  };

  // Inlined rather than reusing contactedWithinSkip: that helper is rebuilt every
  // render, so depending on it would re-run this effect forever.
  useEffect(() => {
    const keep = allRows.filter(({ member }) => {
      const r = recentByUser.get(member.userId);
      return !(r !== undefined && r.days <= skipDays);
    });
    setSelected(new Set(keep.map(({ member }) => member.email)));
  }, [allRows, recentByUser, skipDays]);

  /** What the table shows after search and the group filter. */
  const rows = useMemo(() => {
    const term = foldText(search.trim());
    return allRows.filter(({ member, group }) => {
      if (groupFilter && group.key !== groupFilter) {
        return false;
      }
      if (!term) {
        return true;
      }
      return foldText(member.name).includes(term) || foldText(member.email).includes(term);
    });
  }, [allRows, search, groupFilter]);

  const messageFor = (row: Row): string =>
    fillMessage(templates[row.group.key] ?? row.group.template, row.member);

  const skippedCount = allRows.filter(({ member }) => contactedWithinSkip(member.userId)).length;

  const toggle = (email: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });

  const toggleAllVisible = (on: boolean) =>
    setSelected((cur) => {
      const next = new Set(cur);
      for (const { member } of rows) {
        if (on) {
          next.add(member.email);
        } else {
          next.delete(member.email);
        }
      }
      return next;
    });

  /** Selected members paired with their filled message, for bulk send + CSV. */
  const recipients = allRows
    .filter(({ member }) => selected.has(member.email))
    .map((row) => ({ member: row.member, message: messageFor(row) }));

  const onSendBulk = async () => {
    if (recipients.length === 0) {
      enqueueSnackbar("Select at least one member.", { variant: "info" });
      return;
    }
    // Guard: if she's re-ticked anyone messaged recently, confirm before sending.
    const recentlySelected = recipients.filter((r) => contactedWithinSkip(r.member.userId));
    let overrideGuard = false;
    if (recentlySelected.length > 0) {
      const ok = window.confirm(
        `${recentlySelected.length} selected member(s) were messaged in the last ${skipDays} days. ` +
          "Send anyway?",
      );
      if (!ok) {
        return;
      }
      overrideGuard = true;
    }
    try {
      const { data: res } = await runSend({
        variables: {
          input: {
            recipients: recipients.map((r) => ({ email: r.member.email, message: r.message })),
            campaign,
            source: "CANCELLATIONS",
            // Rechecked server-side against the log just before sending.
            skipContactedWithinDays: overrideGuard ? null : skipDays,
          },
        },
      });
      const r = res?.chatAutomationsSendPersonalMessages;
      if (r?.sendId) {
        setSendId(r.sendId);
        setReviewing(false);
        enqueueSnackbar(
          `Queued for ${r.total} member(s).` +
            (r.skipped?.length ? ` ${r.skipped.length} skipped — messaged too recently.` : ""),
          { variant: "info" },
        );
        return;
      }
      const skipped = r?.skipped?.length ?? 0;
      enqueueSnackbar(
        `Sent to ${r?.succeeded ?? 0} member${r?.succeeded === 1 ? "" : "s"}.` +
          (r?.failed ? ` ${r.failed} failed.` : "") +
          (skipped ? ` ${skipped} skipped — messaged too recently.` : ""),
        { variant: r?.failed || skipped ? "warning" : "success" },
      );
      setReviewing(false);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Send failed.", {
        variant: "error",
      });
    }
  };

  const onDownload = () => {
    const header =
      kind === "trial"
        ? [
            "Name",
            "Email",
            "Country",
            "Trial ends",
            "Days left",
            "Events attended",
            "Group",
            "Message",
          ]
        : ["Name", "Email", "Country", "Ends", "Days left", "Plan", "Group", "Message"];
    const csvRows: string[][] = [header];
    for (const row of allRows) {
      const { member, group } = row;
      csvRows.push(
        kind === "trial"
          ? [
              member.name,
              member.email,
              member.country ?? "",
              member.endDate,
              member.daysLeft?.toString() ?? "",
              member.eventsAttended?.toString() ?? "0",
              group.title,
              messageFor(row),
            ]
          : [
              member.name,
              member.email,
              member.country ?? "",
              member.endDate,
              member.daysLeft?.toString() ?? "",
              member.planId ?? "",
              `${group.title} — ${group.subtitle ?? ""}`,
              messageFor(row),
            ],
      );
    }
    downloadCsv(`cancellations_${kind}.csv`, csvRows);
  };

  const openRow = rows.find(({ member }) => member.email === openFor);
  const allVisibleOn = rows.length > 0 && rows.every(({ member }) => selected.has(member.email));

  return (
    <Stack spacing={Spacing.m} sx={{ minWidth: 0 }}>
      {sendId && (
        <BulkSendProgressPanel
          sendId={sendId}
          onComplete={() =>
            void loadRecent({
              variables: { userIds: allRows.map(({ member }) => member.userId), withinDays: 60 },
            })
          }
        />
      )}
      <Box display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="🔎 Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220, flexGrow: 1 }}
        />
        <TextField
          select
          size="small"
          label="Group"
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All groups</MenuItem>
          {groups.map((group) => (
            <MenuItem key={group.key} value={group.key}>
              {group.title} ({group.count})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Ending within"
          type="number"
          size="small"
          value={days}
          onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))}
          slotProps={{ htmlInput: { min: 1, style: { width: 64 } } }}
          helperText="days"
        />
        <TextField
          label="Skip if messaged within"
          type="number"
          size="small"
          value={skipDays}
          onChange={(e) => setSkipDays(Math.max(0, Number(e.target.value) || 0))}
          slotProps={{ htmlInput: { min: 0, style: { width: 64 } } }}
          helperText="days (any chat DM)"
        />
        <Button
          variant="outlined"
          onClick={() => void load({ variables: { days } })}
          disabled={loading}
        >
          {loading ? "Loading…" : "↻ Refresh"}
        </Button>
      </Box>

      {loading && <Typography variant="body2">Loading…</Typography>}

      {result && allRows.length === 0 && !loading && (
        <Alert severity="info">
          No {kind === "trial" ? "trial" : "scheduled"} cancellations ending in the next {days}{" "}
          days.
        </Alert>
      )}

      {allRows.length > 0 && (
        <>
          {skippedCount > 0 && (
            <Alert severity="warning">
              {skippedCount} member{skippedCount === 1 ? " was" : "s were"} already sent a chat
              message in the last {skipDays} days (by any tool) and{" "}
              {skippedCount === 1 ? "is" : "are"} unselected, so we don't pile on. Re-tick anyone
              you still want to reach.
            </Alert>
          )}

          <Box display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap">
            <Typography variant="body2" color="textSecondary">
              Showing {rows.length} of {allRows.length} · {recipients.length} selected
            </Typography>
            {/* Opens the review rather than sending — see BulkReviewPanel. */}
            <Button
              variant="contained"
              onClick={() => {
                setOpenFor("");
                setReviewing(true);
              }}
              disabled={sending || recipients.length === 0}
            >
              Review {recipients.length} message{recipients.length === 1 ? "" : "s"}
            </Button>
            <Button variant="text" onClick={() => setShowWording((v) => !v)}>
              {showWording ? "Hide wording" : "Edit wording"}
            </Button>
            <Button variant="text" onClick={onDownload}>
              Download CSV
            </Button>
          </Box>

          {/* The per-group templates, tucked away — the row buttons are the
              everyday path, and this is for changing what everyone gets. */}
          {showWording &&
            groups.map((group) => (
              <TextField
                key={group.key}
                label={`${group.title} — {name} / {end_date} / {link} are filled in per member`}
                value={templates[group.key] ?? group.template}
                onChange={(e) => setTemplates((cur) => ({ ...cur, [group.key]: e.target.value }))}
                multiline
                minRows={4}
                fullWidth
              />
            ))}

          <ResponsiveTable minWidth={720}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allVisibleOn}
                    indeterminate={
                      !allVisibleOn && rows.some(({ member }) => selected.has(member.email))
                    }
                    onChange={(e) => toggleAllVisible(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>{kind === "trial" ? "Trial ends" : "Ends"}</TableCell>
                <TableCell align="right">Days left</TableCell>
                <TableCell align="right">{kind === "trial" ? "Attended" : "Plan"}</TableCell>
                <TableCell>Outreach</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const { member, group } = row;
                const recent = recentByUser.get(member.userId);
                const skip = contactedWithinSkip(member.userId);
                return (
                  <TableRow key={member.userId} hover sx={skip ? { opacity: 0.55 } : undefined}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(member.email)}
                        onChange={() => toggle(member.email)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {member.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {member.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={group.title} />
                    </TableCell>
                    <TableCell>{member.endDate}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={member.daysLeft ?? "—"}
                        color={daysLeftColour(member.daysLeft)}
                        variant={
                          daysLeftColour(member.daysLeft) === "default" ? "outlined" : "filled"
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      {kind === "trial" ? (
                        <Typography variant="body2">{member.eventsAttended ?? 0}</Typography>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {member.planId ? member.planId.replace(/-USD.*$/, "") : "—"}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {recent === undefined ? (
                        <Typography variant="caption" color="textSecondary">
                          never
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color={skip ? "error" : "textSecondary"}
                          fontWeight={skip ? 600 : 400}
                        >
                          💬 {recent.days === 0 ? "today" : `${recent.days}d`}
                          {messageToolLabel(recent.campaign)
                            ? ` · ${messageToolLabel(recent.campaign)}`
                            : ""}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant={openFor === member.email ? "contained" : "outlined"}
                        onClick={() => setOpenFor(openFor === member.email ? "" : member.email)}
                      >
                        💬
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </ResponsiveTable>

          {reviewing && (
            <BulkReviewPanel
              recipients={recipients.map(({ member, message }) => ({
                email: member.email,
                name: member.name,
                message,
                warning: contactedWithinSkip(member.userId)
                  ? `messaged ${recentByUser.get(member.userId)?.days}d ago`
                  : undefined,
              }))}
              sending={sending}
              onSend={onSendBulk}
              onClose={() => setReviewing(false)}
            />
          )}

          {openRow && (
            <MemberMessagePanel
              email={openRow.member.email}
              name={openRow.member.name}
              initialMessage={messageFor(openRow)}
              campaign={campaign}
              warning={
                contactedWithinSkip(openRow.member.userId)
                  ? `She was messaged ${recentByUser.get(openRow.member.userId)?.days}d ago, inside your ${skipDays}-day skip window. Send only if you're sure.`
                  : undefined
              }
              onClose={() => setOpenFor("")}
              onSent={() => void load({ variables: { days } })}
            />
          )}
        </>
      )}
    </Stack>
  );
};

const CancellationsPage: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <>
      <PageHeader title="Cancellations">
        Members whose membership or free trial is about to lapse, soonest first. Each one has a
        ready message matched to why she's leaving — press 💬 on her row to read it, edit it and
        send. Or tick several and message them together.
      </PageHeader>

      <Card sx={{ minWidth: 0 }}>
        <Box px={Spacing.l} pt={Spacing.s}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Scheduled" />
            <Tab label="Trial" />
          </Tabs>
        </Box>
        <Box p={Spacing.l} sx={{ minWidth: 0 }}>
          {tab === 0 ? (
            <CancellationTab
              kind="scheduled"
              query={SCHEDULED_CANCELLATIONS}
              resultField="adminScheduledCancellations"
              campaign="cancellation:scheduled"
            />
          ) : (
            <CancellationTab
              kind="trial"
              query={TRIAL_CANCELLATIONS}
              resultField="adminTrialCancellations"
              campaign="cancellation:trial"
            />
          )}
        </Box>
      </Card>
    </>
  );
};

export default CancellationsPage;
